import { getAccessToken, getActiveSession } from '../../../shared/services/authSession';
import { resampleLinear } from './pcmStreamPlayer';

export const buildConversationVoiceRealtimeUrl = (path: string): string => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const httpBase = apiBase || (typeof window === 'undefined' ? '' : window.location.origin);
  const wsBase = httpBase.replace(/^http/i, 'ws');
  const params = new URLSearchParams();
  const token = getAccessToken();
  const tenantId = getActiveSession()?.tenantId?.trim();
  if (token) {
    params.set('access_token', token);
  }
  if (tenantId) {
    params.set('tenantId', tenantId);
  }
  const query = params.toString();
  return `${wsBase}${path}${query ? `?${query}` : ''}`;
};

const floatToPcm16 = (input: Float32Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(input.length * 2);
  const view = new DataView(buffer);
  for (let index = 0; index < input.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]));
    view.setInt16(index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return buffer;
};

export const startPcmCapture = (
  stream: MediaStream,
  targetSampleRate: number,
  onFrame: (pcm: ArrayBuffer) => void,
): { stop: () => void } => {
  const AudioContextCtor = window.AudioContext
    || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('Web Audio is not supported in this browser.');
  }

  const context = new AudioContextCtor();
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(4096, 1, 1);
  const mute = context.createGain();
  mute.gain.value = 0;
  processor.onaudioprocess = event => {
    const input = event.inputBuffer.getChannelData(0);
    const resampled = resampleLinear(input, context.sampleRate, targetSampleRate);
    if (resampled.length > 0) {
      onFrame(floatToPcm16(resampled));
    }
  };
  source.connect(processor);
  processor.connect(mute);
  mute.connect(context.destination);
  void context.resume().catch(() => undefined);

  return {
    stop: () => {
      processor.onaudioprocess = null;
      try {
        processor.disconnect();
        source.disconnect();
        mute.disconnect();
      } catch {
        // Graph may already be closed.
      }
      void context.close().catch(() => undefined);
    },
  };
};
